import React, { useEffect, useRef, useState } from 'react';
import { FiDownload, FiShare, FiX, FiSmartphone, FiArrowRight } from 'react-icons/fi';

function detectPlatform() {
  if (typeof window === 'undefined') return 'other';
  const ua = navigator.userAgent || '';
  const isIOS =
    /iPad|iPhone|iPod/.test(ua) ||
    (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  if (isIOS) return 'ios';
  if (/Android/i.test(ua)) return 'android';
  return 'other';
}

function isStandalone() {
  if (typeof window === 'undefined') return false;
  try {
    return (
      window.navigator.standalone === true ||
      window.matchMedia('(display-mode: standalone)').matches ||
      window.matchMedia('(display-mode: fullscreen)').matches
    );
  } catch {
    return false;
  }
}

function iosBrowserName() {
  const ua = navigator.userAgent || '';
  if (/CriOS/.test(ua)) return 'chrome';
  if (/FxiOS/.test(ua)) return 'firefox';
  if (/EdgiOS/.test(ua)) return 'edge';
  return 'safari';
}

function InstalarApp({ appName = 'Agorax', apkUrl }) {
  const [instalada, setInstalada] = useState(false);
  const [puedeNativo, setPuedeNativo] = useState(false);
  const [modal, setModal] = useState(false);
  const [verificando, setVerificando] = useState(true);
  const deferredRef = useRef(null);
  const platform = useRef(detectPlatform());
  const apk = apkUrl || process.env.REACT_APP_APK_URL || null;

  useEffect(() => {
    if (isStandalone() || window.__agoraxInstalada) {
      setInstalada(true);
      setVerificando(false);
      return;
    }

    // El script inline de index.html puede haber capturado el prompt antes que React.
    if (window.__agoraxDeferredPrompt) {
      deferredRef.current = window.__agoraxDeferredPrompt;
      setPuedeNativo(true);
    }

    const onBip = (e) => {
      e.preventDefault();
      deferredRef.current = e;
      window.__agoraxDeferredPrompt = e;
      setPuedeNativo(true);
    };
    const onInstalled = () => {
      deferredRef.current = null;
      window.__agoraxDeferredPrompt = null;
      window.__agoraxInstalada = true;
      setInstalada(true);
      setModal(false);
    };

    window.addEventListener('beforeinstallprompt', onBip);
    window.addEventListener('appinstalled', onInstalled);

    setVerificando(false);

    return () => {
      window.removeEventListener('beforeinstallprompt', onBip);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  if (verificando || instalada) return null;

  const instalarNativo = async () => {
    const p = deferredRef.current;
    if (p) {
      let outcome = null;
      try {
        p.prompt();
        const choice = await p.userChoice;
        outcome = choice && choice.outcome;
      } catch (e) {
        outcome = null;
      }
      // El evento beforeinstallprompt solo puede usarse una vez.
      deferredRef.current = null;
      window.__agoraxDeferredPrompt = null;
      if (outcome === 'accepted') {
        window.__agoraxInstalada = true;
        setInstalada(true);
        setModal(false);
        return true;
      }
    }
    setModal(true);
    return false;
  };

  const handleFabClick = () => {
    if (platform.current === 'ios') {
      setModal(true);
      return;
    }
    if (puedeNativo && deferredRef.current) {
      instalarNativo();
      return;
    }
    setModal(true);
  };

  const descargarApk = () => {
    if (!apk) return;
    const a = document.createElement('a');
    a.href = apk;
    a.download = '';
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
  };

  const cerrar = () => setModal(false);

  const esIOS = platform.current === 'ios';
  const esAndroid = platform.current === 'android';
  const navegadorIOS = esIOS ? iosBrowserName() : 'safari';

  return (
    <>
      <button
        onClick={handleFabClick}
        style={S.fab}
        aria-label={esAndroid ? `Añadir ${appName} a inicio` : `Instalar ${appName}`}
      >
        <FiDownload size={18} strokeWidth={2.5} />
        <span>{esAndroid ? 'Añadir a inicio' : 'Instalar app'}</span>
      </button>

      {modal && (
        <div style={S.overlay} onClick={cerrar}>
          <div style={S.sheet} onClick={(e) => e.stopPropagation()}>
            <div style={S.sheetHead}>
              <div style={S.iconBadge}>
                <FiSmartphone size={22} />
              </div>
              <button onClick={cerrar} style={S.cerrar} aria-label="Cerrar">
                <FiX size={20} />
              </button>
            </div>

            <div style={S.titulo}>{esAndroid ? `Añadir a inicio` : `Instalar ${appName}`}</div>
            <div style={S.subtitulo}>
              {esIOS
                ? 'Añadela a tu pantalla de inicio para usarla como una app.'
                : esAndroid
                ? 'Toca el botón y confirma para tener el icono en tu pantalla de inicio.'
                : 'Instálala en tu teléfono para abrirla sin navegador.'}
            </div>

            {esIOS && navegadorIOS !== 'safari' && (
              <div style={S.aviso}>
                Abre esta página en <strong>Safari</strong> para poder instalarla.
              </div>
            )}

            {esIOS && (
              <ol style={S.pasos}>
                <li style={S.paso}>
                  <span style={S.pasoNum}>1</span>
                  <span>
                    Toca el botón <strong>Compartir</strong>{' '}
                    <FiShare size={14} style={{ verticalAlign: '-2px' }} /> (cuadro con flecha) de la
                    barra inferior.
                  </span>
                </li>
                <li style={S.paso}>
                  <span style={S.pasoNum}>2</span>
                  <span>
                    Desplázate y toca <strong>Añadir a pantalla de inicio</strong>.
                  </span>
                </li>
                <li style={S.paso}>
                  <span style={S.pasoNum}>3</span>
                  <span>
                    Toca <strong>Añadir</strong>. La app aparecerá en tu pantalla.
                  </span>
                </li>
              </ol>
            )}

            {!esIOS && puedeNativo && (
              <button
                onClick={instalarNativo}
                style={S.btnPrincipal}
                autoFocus
              >
                <FiDownload size={18} />
                Instalar ahora
              </button>
            )}

            {!esIOS && apk && (
              <button onClick={descargarApk} style={S.btnApk}>
                <FiDownload size={18} />
                Descargar APK (Android)
              </button>
            )}

            {!esIOS && !puedeNativo && !apk && (
              <ol style={S.pasos}>
                <li style={S.paso}>
                  <span style={S.pasoNum}>1</span>
                  <span>
                    Abre el menú <strong>⋮</strong> de tu navegador.
                  </span>
                </li>
                <li style={S.paso}>
                  <span style={S.pasoNum}>2</span>
                  <span>
                    Toca <strong>Instalar app</strong> o{' '}
                    <strong>Añadir a pantalla de inicio</strong>.
                  </span>
                </li>
              </ol>
            )}

            {esAndroid && (puedeNativo || apk) && (
              <div style={S.ayuda}>
                ¿No ves la opción? Abre el menú <strong>⋮</strong> de tu navegador y toca{' '}
                <strong>Instalar app</strong> o <strong>Añadir a pantalla de inicio</strong>.
              </div>
            )}

            <button onClick={cerrar} style={S.btnSecundario}>
              Entendido
              <FiArrowRight size={16} />
            </button>
          </div>
        </div>
      )}
    </>
  );
}

const S = {
  fab: {
    position: 'fixed',
    bottom: 'calc(84px + env(safe-area-inset-bottom, 0px))',
    right: '16px',
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-surface)',
    border: 'none',
    borderRadius: '24px',
    padding: '12px 18px',
    fontSize: '14px',
    fontWeight: 700,
    cursor: 'pointer',
    boxShadow: '0 8px 28px rgba(11,26,74,0.35)',
    zIndex: 1500,
    letterSpacing: '-0.2px',
    fontFamily: 'inherit',
    WebkitTapHighlightColor: 'transparent',
    animation: 'slideUp 0.3s ease',
  },
  overlay: {
    position: 'fixed',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: 'rgba(0,0,0,0.5)',
    display: 'flex',
    alignItems: 'flex-end',
    justifyContent: 'center',
    zIndex: 3000,
    animation: 'fadeIn 0.2s ease',
  },
  sheet: {
    width: '100%',
    maxWidth: '480px',
    backgroundColor: 'var(--color-surface)',
    borderTopLeftRadius: '24px',
    borderTopRightRadius: '24px',
    padding: '24px 24px calc(24px + env(safe-area-inset-bottom, 0px))',
    boxShadow: '0 -8px 40px rgba(0,0,0,0.2)',
    animation: 'sheetUp 0.28s ease',
  },
  sheetHead: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
  },
  iconBadge: {
    width: '52px',
    height: '52px',
    borderRadius: '14px',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-surface)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  cerrar: {
    width: '36px',
    height: '36px',
    borderRadius: '50%',
    border: 'none',
    backgroundColor: 'var(--color-bg)',
    color: 'var(--color-text-secondary)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  titulo: {
    fontSize: '20px',
    fontWeight: 800,
    color: 'var(--color-primary)',
    letterSpacing: '-0.3px',
  },
  subtitulo: {
    fontSize: '14px',
    color: 'var(--color-text-secondary)',
    marginTop: '4px',
    marginBottom: '16px',
    lineHeight: 1.45,
  },
  aviso: {
    padding: '12px 14px',
    borderRadius: '12px',
    backgroundColor: '#fef7e0',
    border: '1px solid #fdd835',
    fontSize: '13px',
    color: 'var(--color-text-secondary)',
    marginBottom: '14px',
    lineHeight: 1.4,
  },
  pasos: {
    listStyle: 'none',
    padding: 0,
    margin: '0 0 18px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  },
  paso: {
    display: 'flex',
    gap: '12px',
    alignItems: 'flex-start',
    fontSize: '14px',
    color: 'var(--color-text)',
    lineHeight: 1.4,
  },
  pasoNum: {
    flexShrink: 0,
    width: '24px',
    height: '24px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-secondary)',
    color: 'var(--color-surface)',
    fontSize: '12px',
    fontWeight: 700,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginTop: '1px',
  },
  btnPrincipal: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '14px',
    borderRadius: '14px',
    border: 'none',
    backgroundColor: 'var(--color-primary)',
    color: 'var(--color-surface)',
    fontSize: '15px',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    marginBottom: '10px',
  },
  btnApk: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
    padding: '14px',
    borderRadius: '14px',
    border: '1px solid var(--color-primary)',
    backgroundColor: 'transparent',
    color: 'var(--color-primary)',
    fontSize: '15px',
    fontWeight: 700,
    cursor: 'pointer',
    fontFamily: 'inherit',
    marginBottom: '10px',
  },
  ayuda: {
    fontSize: '12px',
    color: 'var(--color-text-muted)',
    marginBottom: '14px',
    lineHeight: 1.5,
  },
  btnSecundario: {
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    padding: '12px',
    borderRadius: '14px',
    border: 'none',
    backgroundColor: 'var(--color-bg)',
    color: 'var(--color-text-secondary)',
    fontSize: '14px',
    fontWeight: 600,
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
};

export default InstalarApp;
